import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_JES,
  RECEIVE_ALL_JES,
  RECEIVE_JE_METADATA,
  RECEIVE_JE_FAILURE,
  INCREMENT_JE_COUNT,
  RECEIVE_JE_LINES,
  RECEIVE_ALL_JE_LINES,
  RECEIVING_JE_LINES,
  RECEIVE_JE_LINES_METADATA,
  RECEIVE_JE_LINES_FAILURE,
  SAVING_JE_LINES,
  SAVING_JE_LINES_FAILURE,
  UPDATING_JE_LINES,
  UPDATING_JE_LINES_FAILURE,
  CLEAR_JE_STATE,
  CLEAR_STATE,
  CLEAR_JE_UPDATE_ERR_STATE,
  UPDATE_CURRENT_JE_PAGE,
  RESET_JE_META,
  DELETE_JE_LINES,
  CLEAR_JE_TRX_LINES,
  FILTER_JE_QUERY,
  CLEAR_FILTER_JE_QUERY,
  FILTER_PR_QUERY,
  CLEAR_FILTER_PR_QUERY,
  ACTIVE_TAB,
  RESET_ACTIVE_TAB,
  OFFSET_EXISTED_JES
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import { fetchLinesNew, getTrxIds } from '@/store/modules/helpers/actionHelpers'
import {
  receiveData,
  increment,
  receiveMetadata,
  resetMetadata,
  receiveLinesMetadata,
  deleteLine,
  clearTrxLines
} from '@/store/modules/helpers/mutationHelpers'

const jeSchema = new schema.Entity('jes')
const jeListSchema = new schema.Array(jeSchema)

const lineSchema = new schema.Entity('lines')
const lineListSchema = new schema.Array(lineSchema)

const state = {
  data: {},
  all: [],
  allLines: [],
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  receiveDataFailure: false,
  lines: null,
  lineCount: null,
  nextLineUrl: null,
  prevLineUrl: null,
  receivingLines: null,
  savingLines: null,
  updatingLines: null,
  filterInfo: {},
  filterInfoPr: {},
  activeTab: 'open',
  offsetJEStatusById: {}
}

const actions = {
  /**
   * Fetches a single JE by ID. If storeLines is false, lines will not be fetched and committed to the store.
   * @param store
   * @param id
   * @param storeLines
   * @return {Promise<any>}
   */
  async fetchJeById (store, { id, storeLines }) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.get(buildUrlWithQuery(`jes/${id}?nest_lines=false`, null, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!(resp && resp.id)) {
        throw new Error('No response')
      }
      const { entities } = normalize(resp, jeSchema)
      this.commit(RECEIVE_JES, { next: '', data: entities.jes })
      if (storeLines) {
        const lines = await fetchLinesNew.bind(this)({ trxIds: getTrxIds(entities.jes), type: 'je' })
        this.commit(RECEIVE_JE_LINES, { id: null, data: lines })
      }
      return entities
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Fetches one or more JEs with the option to pass query string filter parameters. If storeLines is false, lines will
   * not be fetched and committed to the store.
   * @param store
   * @param opts
   * @return {Promise<*>}
   */
  async fetchCompactLineJesWithQuery (store, opts) {
    let query
    let storeLines = false
    if (opts) {
      query = opts.hasOwnProperty('query') ? opts.query : opts
      storeLines = opts.hasOwnProperty('storeLines') ? opts.storeLines : false
    } else {
      query = {}
    }
    query.nest_lines = false
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.get(buildUrlWithQuery('jes', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(RESET_JE_META)
      let results = []
      results = Object.values(resp.je_original_transactions) || []

      let entities = {
        jes_original: resp.je_original_transactions || {},
        jes: resp.je_transactions || {},
        lines: Object.values(resp.je_original_lines || {}) || [],
        je_lines: Object.values(resp.je_lines || {}) || []
      }
      let tnxID = Object.keys(entities.jes_original) && Object.keys(entities.jes_original).length > 0 ? Object.keys(entities.jes_original)[0] : null
      this.commit(OFFSET_EXISTED_JES, {id: tnxID, offset: entities.je_lines.length > 0})
      const yieldToBrowser = () => new Promise(resolve => requestAnimationFrame(resolve))
      let count = 1
      let next = null
      let previous = null
      if (!(entities && (Array.isArray(Object.values(results)))) || !results.length) {
        throw new Error('No result set in response')
      }
      let mutationPayload = { data: entities.jes_original }
      if (query && query.page && typeof query.page === 'number') {  // If query contains a page number
        mutationPayload.page = query.page                           // pass the page number to the mutation handler
      }
      // Commit DC JEs
      let mutationJEPayload = { data: entities.jes }
      this.commit(RECEIVE_JES, mutationJEPayload)
      await yieldToBrowser()
      // Commit Original JEs
      this.commit(RECEIVE_JES, mutationPayload)
      await yieldToBrowser()
      // Commit JE Lines
      this.commit(RECEIVE_JE_LINES, { id: null, data: entities.je_lines })
      await yieldToBrowser()
      this.commit(RECEIVE_JE_METADATA, { next, previous, count, result: 1 })
      await yieldToBrowser()
      this.commit(UPDATE_CURRENT_JE_PAGE, query && query.page || 1)
      await yieldToBrowser()
      if (storeLines) {
        // const lines = await fetchLinesNew.bind(this)({ trxIds: getTrxIds(entities.jes), type: 'je' })
        this.commit(RECEIVE_JE_LINES, { id: null, data: entities.lines })
        await yieldToBrowser()
      }
      return { query, count, entities, results, previous, next }
    } catch (e) {
      console.log(e)
      this.commit(RECEIVE_JE_FAILURE, handleXHRError(e))
    }
  },
  async fetchJesWithQuery (store, opts) {
    let query
    let storeLines = false
    if (opts) {
      query = opts.hasOwnProperty('query') ? opts.query : opts
      storeLines = opts.hasOwnProperty('storeLines') ? opts.storeLines : false
    } else {
      query = {}
    }
    query.nest_lines = false
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      if (query.hasOwnProperty('page') && (query.page <= 0 || isNaN(query.page))) query.page = 1
      const resp = await Vue.http.get(buildUrlWithQuery('jes', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(RESET_JE_META)
      const { results, next, previous, count } = resp
      const { entities, result } = normalize(results, jeListSchema)
      if (!(result && Array.isArray(result))) {
        throw new Error('No result set in response')
      }
      let mutationPayload = { data: entities.jes }
      if (query && query.page && typeof query.page === 'number') {  // If query contains a page number
        mutationPayload.page = query.page                           // pass the page number to the mutation handler
      }
      this.commit(RECEIVE_JES, mutationPayload)
      this.commit(RECEIVE_JE_METADATA, { next, previous, count, result: result.length })
      this.commit(UPDATE_CURRENT_JE_PAGE, query && query.page || 1)
      if (storeLines) {
        const lines = await fetchLinesNew.bind(this)({ trxIds: getTrxIds(entities.jes), type: 'je' })
        this.commit(RECEIVE_JE_LINES, { id: null, data: lines })
      }
      return { query, count, entities, results, previous, next }
    } catch (e) {
      console.log(e)
      if (e.status === 403) {
        window.location.href = '/403'
      }
      this.commit(RECEIVE_JE_FAILURE, handleXHRError(e))
    }
  },
  fetchAllJes (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl('jes?serialize=false&nest_lines=false', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(response => {
        if (!response || !Array.isArray(response.body)) return this.commit(RECEIVE_JE_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_JES, { data: response.body })
        return response.body
      })
      .catch(e => {
        this.commit(RECEIVE_JE_FAILURE, e.statusText)
      })
  },
  fetchAllJeLines (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_JE_LINES, true)
    return Vue.http.get(buildUrl('lines/je?serialize=false', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(response => {
        this.commit(RECEIVING_JE_LINES, false)
        if (!response || !Array.isArray(response.body)) return this.commit(RECEIVE_JE_LINES_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_JE_LINES, { data: response.body })
        return response.body
      })
      .catch(e => {
        this.commit(RECEIVING_JE_LINES, false)
        this.commit(RECEIVE_JE_LINES_FAILURE, e.statusText)
      })
  },
  /**
   * Saves a JE transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async saveJe (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.post(buildUrl('jes', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to save JE`)
      }
      const { entities, result } = normalize(resp, jeSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when saving JE`)
      }
      this.commit(RECEIVE_JES, { data: entities.jes })
      if (resp && resp.lines) {
        const e = normalize(resp.lines, lineListSchema)
        this.commit(RECEIVE_JE_LINES, { id: resp.id, data: e.entities.lines })
      }
      this.commit(INCREMENT_JE_COUNT, 1)
      return resp
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Updates a JE transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async updateJe (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.patch(buildUrl(`jes/${id}`, null, instanceUrl), data, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to update JE with id ${id}`)
      }
      const { entities, result } = normalize(resp, jeSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when updating JE with id ${id}`)
      }
      this.commit(RECEIVE_JES, { data: entities.jes })
      if (resp && resp.lines) {
        const e = normalize(resp.lines, lineListSchema)
        this.commit(RECEIVE_JE_LINES, { id: resp.id, data: e.entities.lines })
      }
      return resp
    } catch (e) {
      console.log(e)
      return handleXHRError(e)
    }
  },
  clearJeState (store, payload = null) {
    this.commit(CLEAR_JE_STATE, payload)
    return true
  },
  clearJeTransactionLines (store, apId) {
    this.commit(CLEAR_JE_TRX_LINES, apId)
  },
  resetJeUpdateErrState () {
    return this.commit(CLEAR_JE_UPDATE_ERR_STATE)
  },
  resetJeMeta () {
    return this.commit(RESET_JE_META)
  },
  setJeFilterInfo (store, query) {
    this.commit(FILTER_JE_QUERY, query)
  },
  clearSetJeFilterInfo (store) {
    this.commit(CLEAR_FILTER_JE_QUERY)
  },
  setPrFilterInfo (store, query) {
    this.commit(FILTER_PR_QUERY, query)
  },
  clearSetPrFilterInfo (store) {
    this.commit(CLEAR_FILTER_PR_QUERY)
  },
  setActiveTab (store, tab) {
    this.commit(ACTIVE_TAB, tab)
  },
  resetActiveTab () {
    this.commit(RESET_ACTIVE_TAB)
  }
}

const getters = {
  getActiveTab (state) {
    return state.activeTab
  },
  getJeFilterInfo: (state) => {
    return state.filterInfo
  },
  getPrFilterInfo: (state) => {
    return state.filterInfoPr
  },
  getJes: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getJesCache: (state) => {
    return state.cache ? Object.values(state.cache).map(je => Object.assign({}, je)) : []
  },
  getJeById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllJes: (state) => {
    return state.all || []
  },
  getAllJeLines: (state) => {
    return state.allLines || []
  },
  getJeMetadata: (state) => {
    const { shown, count, next, previous } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown, next, previous }
  },
  getJeByPropertyAndValue: (state) => (propObject) => {
    if (!state.data) return []
    const scanPropArray = (propName, propArray) => {
      let matches = []
      for (let i = 0; i < propArray.length; i++) {
        matches.push(...Object.values(state.data).filter((v) => {
          if (v[propName] === propArray[i]) {
            v.transactionType = 'Journal Entry'
            return v
          }
        }))
      }
      return matches
    }
    let ret = []
    for (let p in propObject) {
      if (Array.isArray(propObject[p])) {
        ret.push(...scanPropArray(p, propObject[p]))
      } else {
        state.data.map((v) => {
          if (!v[p]) return null
          if (v[p] === propObject[p]) {
            v.transactionType = 'Journal Entry'
            ret.push(v)
          }
        })
      }
    }
    return ret
  },
  getJeLines: (state) => {
    return state.lines || {}
  },
  getJeLineById: (state) => (id) => {
    const { lines } = state
    for (let l in lines) {
      if (!lines.hasOwnProperty(l)) continue
      if (parseInt(lines[l].id) === parseInt(id)) return lines[l]
    }
    return {}
  },
  getJeLinesByTransactionId: (state) => (id) => {
    const { lines } = state
    if (!state.lines || !Object.keys(state.lines).length) return {}
    let ret = {}
    for (let l in state.lines) {
      if (lines[l].hasOwnProperty('transaction_id') && parseInt(lines[l].transaction_id) === parseInt(id)) {
        ret[l] = lines[l]
      }
    }
    return ret
  },
  getNextJeLineSet: (state) => {
    return state.nextLineUrl
  },
  getPrevJeLineSet: (state) => {
    return state.prevLineUrl
  },
  getCurrentJePage: (state) => {
    if (state.currentPage) return state.currentPage
  },
  getOffsetJEStatusById: (state) => (id) => {
    return state.offsetJEStatusById[id] || false
  }
}

const mutations = {
  [OFFSET_EXISTED_JES] (state, payload) {
    const { id, offset } = payload
    if (id) state.offsetJEStatusById[id] = offset
  },
  [CLEAR_FILTER_JE_QUERY] (state) {
    state.filterInfo = {}
  },
  [CLEAR_FILTER_PR_QUERY] (state) {
    state.filterInfoPr = {}
  },
  [FILTER_JE_QUERY] (state, query) {
    if (query.hasOwnProperty('keyword')) {
      state.filterInfo.keyword = query.keyword
    }
    if (query.hasOwnProperty('status__in')) {
      state.filterInfo.status__in = query.status__in
    }
    if (query.hasOwnProperty('batch')) {
      state.filterInfo.batch = query.batch
    }
    if (query.hasOwnProperty('source_code__in')) {
      state.filterInfo.source_code__in = query.source_code__in
    }
    if (query.hasOwnProperty('view_voids')) {
      state.filterInfo.view_voids = query.view_voids
    }
    if (query.hasOwnProperty('view_dc')) {
      state.filterInfo.view_dc = query.view_dc
    }
    if (query.hasOwnProperty('page')) {
      state.filterInfo.page = query.page
    }
    if (query.hasOwnProperty('batch__bank__currency__in')) {
      state.filterInfo.batch__bank__currency__in = query.batch__bank__currency__in
    }
  },
  [FILTER_PR_QUERY] (state, query) {
    if (query.hasOwnProperty('keyword')) {
      state.filterInfoPr.keyword = query.keyword
    }
    if (query.hasOwnProperty('status__in')) {
      state.filterInfoPr.status__in = query.status__in
    }
    if (query.hasOwnProperty('batch')) {
      state.filterInfoPr.batch = query.batch
    }
    if (query.hasOwnProperty('source_code')) {
      state.filterInfoPr.source_code = query.source_code
    }
    if (query.hasOwnProperty('page')) {
      state.filterInfoPr.page = query.page
    }
    if (query.hasOwnProperty('batch__bank__currency__in')) {
      state.filterInfoPr.batch__bank__currency__in = query.batch__bank__currency__in
    }
  },
  [RECEIVE_JES] (state, results) {
    for (let d in results.data) {
      results.data[d]['transactionType'] = 'Journal Entry'
    }
    receiveData(state, results)
  },
  [RECEIVE_ALL_JES] (state, results) {
    const { data } = results
    state.all = data
    return true
  },
  [RECEIVE_JE_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [RECEIVE_JE_LINES] (state, payload) {
    const { id, data } = payload
    if (!(data && (Array.isArray(data) || Object.keys(data).length))) {
      return state.lines
    }
    let tempLineState = { ...state.lines }
    let newData = {}
    if (Array.isArray(data) && data.every(line => !!line.id)) {
      data.forEach(line => (newData[line.id] = line))
    } else {
      newData = data
    }
    if (tempLineState && Object.keys(tempLineState).length) {
      tempLineState = { ...tempLineState, ...newData }
    } else {
      tempLineState = { ...newData }
    }
    if (id) {
      let tempState = { ...state.data }
      for (let d in newData) {
        if (tempState[id] && tempState[id].hasOwnProperty('lines') && Array.isArray(tempState[id].lines)) {
          tempState[id].lines = [...new Set([...tempState[id].lines, newData[d].id])]
        }
      }
      state.data = { ...tempState }
    }
    state.lines = { ...tempLineState }
  },
  [RECEIVING_JE_LINES] (state, payload) {
    state.receivingLines = { status: payload, err: null }
  },
  [RECEIVE_ALL_JE_LINES] (state, results) {
    const { data } = results
    state.allLines = data
    return true
  },
  [RECEIVE_JE_LINES_FAILURE] (state, payload) {
    state.receivingLines = { status: false, err: payload }
  },
  [SAVING_JE_LINES] (state, payload) {
    state.savingLines = { status: payload, err: null }
  },
  [SAVING_JE_LINES_FAILURE] (state, payload) {
    state.savingLines = { status: false, err: payload }
  },
  [UPDATING_JE_LINES] (state, payload) {
    state.updatingLines = { status: payload, err: null }
  },
  [UPDATING_JE_LINES_FAILURE] (state, payload) {
    state.updatingLines = { status: false, err: payload }
  },
  [INCREMENT_JE_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_JE_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [RECEIVE_JE_LINES_METADATA] (state, payload) {
    receiveLinesMetadata(state, payload)
  },
  [UPDATE_CURRENT_JE_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_JE_STATE] (state, payload) {
    let newState = {
      data: Object.assign({}),
      lines: Object.assign({}),
      saving: false,
      receiveDataFailure: false,
      updating: false,
      updateFailure: false,
      deleting: false
    }
    if (payload) {
      if (Array.isArray(payload) && payload.length) {
        payload.forEach(v => {
          if (newState.hasOwnProperty(v)) delete newState[v]
        })
      } else {
        newState['cache'] = Object.assign({})
      }
    }
    for (let key in newState) {
      state[key] = newState[key]
    }
  },
  [CLEAR_JE_TRX_LINES] (state, payload) {
    clearTrxLines(state, payload)
  },
  [RESET_JE_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    state.data = Object.assign({})
    state.all = []
    state.allLines = []
    state.cache = Object.assign({})
    state.count = null
    state.shown = null
    state.next = null
    state.previous = null
    state.currentPage = 1
    state.receiveDataFailure = false
    state.lines = null
    state.lineCount = null
    state.nextLineUrl = null
    state.prevLineUrl = null
    state.receivingLines = null
    state.savingLines = null
    state.updatingLines = null
  },
  [CLEAR_JE_UPDATE_ERR_STATE] (state) {
    state.updateFailure = null
  },
  [DELETE_JE_LINES] (state, payload) {
    deleteLine(state, payload)
  },
  [ACTIVE_TAB] (state, payload) {
    state.activeTab = payload
  },
  [RESET_ACTIVE_TAB] (state) {
    state.activeTab = 'open'
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/je.js